// src/firebase/users.js
// User Management Service - Item 6: Add more markers

import { 
  collection, 
  doc, 
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from './config';
import { getCurrentUserOrgId } from './orgs';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

/**
 * Create a new marker account.
 *
 * Done via the createMarkerAccount Cloud Function (Admin SDK), not the
 * client createUserWithEmailAndPassword this used to call directly - that
 * call signs the browser in AS THE NEW ACCOUNT the moment it's created,
 * evicting whoever was actually signed in (the head marker, or a platform
 * admin mid Support Access session). Going through the Admin SDK
 * server-side means the caller's own session is never touched.
 */
export const createMarkerAccount = async (userData) => {
  try {
    const { name, email, role } = userData;

    // Validate role
    if (!['marker', 'head_marker'].includes(role)) {
      throw new Error('Invalid role. Must be "marker" or "head_marker"');
    }

    const functions = getFunctions(getApp());
    const createMarkerAccountFn = httpsCallable(functions, 'createMarkerAccount');
    const result = await createMarkerAccountFn({ name, email, role });

    console.log('Marker account created:', result.data.uid);

    return result.data;

  } catch (error) {
    console.error('Create marker account error:', error);
    // The Cloud Function already throws user-friendly HttpsError messages
    // (e.g. "This email is already registered.") - error.message carries
    // that text through as-is.
    throw new Error(error.message);
  }
};

/**
 * Get all marker accounts (both marker and head_marker roles)
 * Item 6: User management
 */
export const getAllMarkers = async () => {
  try {
    console.log('Attempting to load markers...');
    const orgId = await getCurrentUserOrgId();
    const q = query(
      collection(db, 'users'),
      where('orgId', '==', orgId),
      where('role', 'in', ['marker', 'head_marker']),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const markers = [];

    snapshot.forEach((doc) => {
      markers.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log('Markers loaded successfully:', markers.length);
    return markers;
  } catch (error) {
    console.error('Get all markers error:', error);
    console.error('Error code:', error.code);

    // If composite index error, try simpler query without orderBy
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      console.log('Falling back to simple query (no orderBy)...');
      try {
        const orgId = await getCurrentUserOrgId();
        const simpleQuery = query(
          collection(db, 'users'),
          where('orgId', '==', orgId),
          where('role', 'in', ['marker', 'head_marker'])
        );
        
        const snapshot = await getDocs(simpleQuery);
        const markers = [];
        
        snapshot.forEach((doc) => {
          markers.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Sort in JavaScript instead
        markers.sort((a, b) => {
          const aDate = a.createdAt?.toDate?.() || new Date(0);
          const bDate = b.createdAt?.toDate?.() || new Date(0);
          return bDate - aDate; // desc order
        });
        
        console.log('Markers loaded via fallback query:', markers.length);
        return markers;
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
        throw fallbackError;
      }
    }
    
    throw error;
  }
};

/**
 * Get marker by ID
 */
export const getMarker = async (userId) => {
  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Marker not found');
    }
    
    return {
      id: docSnap.id,
      ...docSnap.data()
    };
  } catch (error) {
    console.error('Get marker error:', error);
    throw error;
  }
};

/**
 * Update marker account details
 * Item 6: User management
 */
export const updateMarkerAccount = async (userId, updates) => {
  try {
    const docRef = doc(db, 'users', userId);
    
    // Don't allow updating certain fields
    const { uid, createdAt, createdBy, ...safeUpdates } = updates;
    
    await updateDoc(docRef, {
      ...safeUpdates,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || 'system'
    });
    
    console.log('Marker account updated:', userId);
  } catch (error) {
    console.error('Update marker account error:', error);
    throw error;
  }
};

/**
 * Deactivate a marker account
 * Item 6: User management
 */
export const deactivateMarker = async (userId) => {
  try {
    await updateMarkerAccount(userId, { active: false });
    console.log('Marker deactivated:', userId);
  } catch (error) {
    console.error('Deactivate marker error:', error);
    throw error;
  }
};

/**
 * Permanently delete a deactivated marker (see deleteMarkerAccount in
 * functions/index.js) - their past marking is kept, shown as "Former marker"
 */
export const deleteMarker = async (markerId) => {
  const result = await httpsCallable(getFunctions(getApp()), 'deleteMarkerAccount')({ markerId });
  return result.data;
};

/**
 * Reactivate a marker account
 * Item 6: User management
 */
export const reactivateMarker = async (userId) => {
  try {
    await updateMarkerAccount(userId, { active: true });
    console.log('Marker reactivated:', userId);
  } catch (error) {
    console.error('Reactivate marker error:', error);
    throw error;
  }
};

/**
 * Send password reset email to marker
 * Item 6: User management
 */
export const sendMarkerPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    console.log('Password reset email sent to:', email);
  } catch (error) {
    console.error('Send password reset error:', error);
    
    if (error.code === 'auth/user-not-found') {
      throw new Error('No account found with this email');
    } else {
      throw new Error('Failed to send password reset email');
    }
  }
};

/**
 * Get marker statistics
 * Item 6: User management
 */
export const getMarkerStats = async () => {
  try {
    const markers = await getAllMarkers();
    
    const stats = {
      total: markers.length,
      active: markers.filter(m => m.active !== false).length,
      inactive: markers.filter(m => m.active === false).length,
      regularMarkers: markers.filter(m => m.role === 'marker').length,
      headMarkers: markers.filter(m => m.role === 'head_marker').length
    };
    
    return stats;
  } catch (error) {
    console.error('Get marker stats error:', error);
    return {
      total: 0,
      active: 0,
      inactive: 0,
      regularMarkers: 0,
      headMarkers: 0
    };
  }
};